import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { MajButtonComponent, MajGridButtonDirective, MajTooltipDirective, ModalManagerService } from 'majora';
import { LoadingOverlayComponent, LoadingOverlayMethod, ModalBase, ModalBaseDirective } from 'morgana';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { EventsManagerService, EventsManagerSubscription } from '@core/events-manager/events-manager.service';
import { EVENT_MANAGER_CONSTANTS } from '@core/events-manager/events-manager.constants';
import { TodaysPatientsAppointmentResponse } from '@gandalf/model/todays-patients-appointment-response';
import { EstablishedStatus, ScheduleItemStatus } from '@gandalf/constants';
import { DATE_FORMATS } from '@shared/constants/date-format.constants';
import { NavigationService } from '@core/navigation/navigation.service';
import { DialstackWidgetService } from '@core/phone-call/dialstack-widget.service';
import { PatientCallService } from '@core/phone-call/patient-call.service';
import { URL_PRINT_ENDPOINTS } from '@shared/constants/url.constants';
import { UrlService } from '@core/url-util/url.service';
import { StartAppointmentRequest } from '@gandalf/model/start-appointment-request';
import { AppointmentResponse } from '@gandalf/model/appointment-response';
import { EncounterService } from '@core/encounter/encounter.service';
import { SecurityManagerService } from '@core/security-manager/security-manager.service';
import { UserLocationsService } from '@core/user-locations/user-locations.service';
import { _isEmpty, _isNil } from '@core/lodash/lodash';
import { Subscription } from 'rxjs';
import { HIT_PMS_HTML_PREFERENCES } from '@core/legacy/hit-pms-html.constants';
import { FindTodaysPatientsAppointmentsForProviderRequest } from '@gandalf/model/find-todays-patients-appointments-for-provider-request';
import { RapidReviewModalData } from '@shared/component/rapid-review-modal/rapid-review-modal.component';
import { RapidReviewModalService } from '@shared/component/rapid-review-modal/rapid-review-modal.service';
import {
	START_APPOINTMENT_MODAL_OPTIONS,
	StartAppointmentModalComponent
} from '@shared/component/start-appointment-modal/start-appointment-modal.component';
import { CommonModule } from '@angular/common';

import { ConfirmedStatusIconComponent } from '@shared/component/confirmed-status-icon/confirmed-status-icon.component';
import { InsuranceStatusIconComponent } from '@shared/component/insurance-status-icon/insurance-status-icon.component';
import { InterviewStatusIconComponent } from '@shared/component/interview-status-icon/interview-status-icon.component';
import { SignedStatusIconComponent } from '@shared/component/signed-status-icon/signed-status-icon.component';
import { IntToHexColorPipe } from '@shared/pipes/int-to-hex-color/int-to-hex-color.pipe';
import { YearsDiffPipe } from '@shared/pipes/years-diff/years-diff.pipe';
import { NavigationMenuService } from '@header/services/navigation-menu.service';

@Component({
	selector: 'pms-todays-patients-dockable-modal',
	templateUrl: './todays-patients-dockable-modal.component.html',
	styles: [],
	imports: [
		CommonModule,
		DialogModule,
		MajButtonComponent,
		MajGridButtonDirective,
		MajTooltipDirective,
		LoadingOverlayComponent,
		ConfirmedStatusIconComponent,
		InsuranceStatusIconComponent,
		InterviewStatusIconComponent,
		SignedStatusIconComponent,
		IntToHexColorPipe,
		YearsDiffPipe,
		ModalBaseDirective,
	],
})
export class TodaysPatientsDockableModalComponent extends ModalBase implements OnInit {
	private encounterService = inject(EncounterService);
	private eventsManagerService = inject(EventsManagerService);
	private navigationMenuService = inject(NavigationMenuService);
	private navigationService = inject(NavigationService);
	private securityManagerService = inject(SecurityManagerService);
	private userLocationService = inject(UserLocationsService);
	private urlService = inject(UrlService);
	private rapidReviewModalService = inject(RapidReviewModalService);
	private patientCallService = inject(PatientCallService);
	private modalManagerService = inject(ModalManagerService);

	readonly phonesAvailable = inject(DialstackWidgetService).available;

	@ViewChild('modal')
	modal: DialogComponent;

	request = new FindTodaysPatientsAppointmentsForProviderRequest();
	subscribeToToggle: EventsManagerSubscription;
	subscribeToLocationChange: Subscription;
	todaysPatientsAppointmentResponses: TodaysPatientsAppointmentResponse[];
	maximized = true;
	dateFormat = DATE_FORMATS.MM_DD_YYYY;
	scheduleItemStatus = ScheduleItemStatus;
	connectSubscriber: boolean;

	_isLoading: boolean;
	disableStart = false;

	ngOnInit() {
		this.subscribeTodaysPatientsToggle();
		this.subscribeLocationData();
		this.connectSubscriber = this.securityManagerService.allowConnectAppointmentReminders();
	}

	getData() {
		this.findTodaysPatients().subscribe(responses => {
			this.todaysPatientsAppointmentResponses = responses;
		});
	}

	subscribeLocationData() {
		this.subscribeToLocationChange = this.userLocationService.observeCurrentUserLocation().subscribe(() => {
			this.getData();
		});
	}

	findTodaysPatients() {
		if (this.securityManagerService.userIsProvider()
			&& this.securityManagerService.preferenceValueIsOn(
				HIT_PMS_HTML_PREFERENCES.SCHEDULE_MY_PATIENTS_ONLY.name,
				HIT_PMS_HTML_PREFERENCES.SCHEDULE_MY_PATIENTS_ONLY.defaultValue,
			)
		) {
			return this.findTodaysPatientsAppointmentsForProvider();
		} else {
			return this.findTodaysPatientsAppointments();
		}
	}

	@LoadingOverlayMethod()
	findTodaysPatientsAppointments() {
		return this.navigationMenuService.findTodaysPatientsAppointments(this.userLocationService.getCurrentUserLocation().id);
	}

	@LoadingOverlayMethod()
	findTodaysPatientsAppointmentsForProvider() {
		this.request.providerId = this.securityManagerService.getUserProviderId();
		this.request.locationId = this.userLocationService.getCurrentUserLocation().id;
		return this.navigationMenuService.findTodaysPatientsAppointmentsForProvider(this.request);
	}

	closeModal() {
		this.eventsManagerService.unsubscribeEvent(this.subscribeToToggle);
		this.subscribeToLocationChange.unsubscribe();
		this.dynamicModalRef.close(this.modal, null);
	}

	maximize(): void {
		this.getData();
		this.modal.position = {X: 'right', Y: 'top'};
		this.modal.height = '85%';
		this.maximized = true;
	}

	minimize(): void {
		this.modal.position = {X: 'right', Y: 'bottom'};
		this.modal.height = 44;
		this.maximized = false;
	}

	subscribeTodaysPatientsToggle() {
		this.subscribeToToggle = this.eventsManagerService.subscribe(EVENT_MANAGER_CONSTANTS.HEADER.TODAYS_PATIENTS_RESIZE, () => {
			this.todaysPatientsToggle();
		});
	}

	todaysPatientsToggle() {
		if (this.maximized) {
			this.minimize();
		} else {
			this.maximize();
		}
	}

	goToScheduleItem(scheduleItemId: number) {
		this.navigationService.navigateToScheduleAppointment(scheduleItemId);
	}

	async goToPatient(patientId: number) {
		await this.navigationService.navigateToPatient(patientId);
	}

	async goToAppointmentOrEncounter(appointment: TodaysPatientsAppointmentResponse) {
		if (_isNil(appointment.encounterId)) {
			await this.navigationService.navigateToPatientAppointment(appointment.patientName.patientId, appointment.appointmentId);
		} else {
			await this.navigationService.navigateToEncounter(appointment.encounterId, appointment.patientName.patientId);
		}
	}

	callPatient(patientId: number) {
		this.patientCallService.callPreferredNumber(patientId);
	}

	printEncounter(encounterId: number) {
		this.urlService.openTabWithPost(URL_PRINT_ENDPOINTS.ENCOUNTER_SUMMARY, {encounterId});
	}

	async startAppointment(appointment: TodaysPatientsAppointmentResponse) {
		if (this.disableStart) {
			return;
		}
		this.disableStart = true;
		if (_isNil(appointment.provider?.providerId) && _isNil(appointment.employeeName?.employeeId)) {
			return this.startRoleBookedAppointment(appointment);
		}
		const request = new StartAppointmentRequest();
		request.appointmentId = appointment.appointmentId;
		request.employeeId = appointment.employeeName?.employeeId;
		request.providerId = appointment.provider?.providerId;
		this.navigationMenuService.startAppointment(request).subscribe({
			next: async (encounterId) => {
				await this.navigationService.navigateToEncounter(encounterId, appointment.patientName.patientId);
				this.getData();
			},
			error: () => this.disableStart = false,
		});
	}

	private async startRoleBookedAppointment(appointment: TodaysPatientsAppointmentResponse) {
		const appointmentResponse = await this.modalManagerService.open(StartAppointmentModalComponent, {
			providerId: null,
			employeeId: null,
			appointmentId: appointment.appointmentId,
			locationId: this.userLocationService.getCurrentUserLocation().id,
			returnCalendarResponse: false,
		}, START_APPOINTMENT_MODAL_OPTIONS).result as AppointmentResponse;
		if (_isNil(appointmentResponse)) {
			this.disableStart = false;
			return;
		}
		await this.navigationService.navigateToEncounter(appointmentResponse.encounterId, appointmentResponse.patientId);
		this.disableStart = false;
		this.getData();
	}

	completeEncounter(encounterId: number) {
		this.encounterService.completeEncounter(encounterId).subscribe(() => {
			this.getData();
		});
	}

	isPatientNew(establishedStatus: EstablishedStatus) {
		return establishedStatus === EstablishedStatus.NEW;
	}

	connectMessageExistsAndDelivered(connectMessageExists: boolean, connectMessageDelivered: boolean) {
		return this.connectSubscriber && connectMessageExists && connectMessageDelivered;
	}

	connectMessageExistsAndNotDelivered(connectMessageExists: boolean, connectMessageDelivered: boolean) {
		return this.connectSubscriber && connectMessageExists && !connectMessageDelivered;
	}

	showNoDataMessage() {
		if (this._isLoading) {
			return false;
		}
		return _isEmpty(this.todaysPatientsAppointmentResponses) && this.maximized;
	}

	showRapidReviewButton(appointment: TodaysPatientsAppointmentResponse) {
		return this.rapidReviewModalService.canOpen(appointment.encounterStatus, appointment.lastEncounterId);
	}

	openRapidReviewModal(appointment: TodaysPatientsAppointmentResponse) {
		let data: RapidReviewModalData;
		if (!appointment.encounterId) {
			data = {encounterId: appointment.lastEncounterId, isCurrentEncounter: false};
		} else {
			data = {encounterId: appointment.encounterId, isCurrentEncounter: true};
		}

		this.rapidReviewModalService.open(data);
		this.minimize();
	}
}
